

import React, { useState, useEffect, useRef } from 'react';
import { Box, Typography, useTheme, TextField, Button, CircularProgress, IconButton, Dialog, DialogTitle, DialogContent, DialogActions, Snackbar, Alert } from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import { tokens } from "../../theme";
import Header from "../../components/Header";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../AuthContext";
import VisibilityIcon from '@mui/icons-material/Visibility';
import DeleteIcon from '@mui/icons-material/Delete';

const Pumps = () => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const navigate = useNavigate();
  const [selectedRow, setSelectedRow] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalPumps, setTotalPumps] = useState(0);
  const [searchLoading, setSearchLoading] = useState(false);
  const [searchError, setSearchError] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });
  const { pumps, setPumps, setValue, authorizedFetch, formatDuration, deletePump, userRole } = useAuth();
  const canDelete = userRole === "admin" || userRole === "manager";
  const limit = 15; // Number of pumps per page
  const fetchTimeout = useRef(null);

  const handleDeleteConfirm = async () => {
    if (!deleteTarget) return;
    try {
      await deletePump(deleteTarget.imei);
      setSnackbar({ open: true, message: `Pump ${deleteTarget.imei} deleted`, severity: "success" });
      setDeleteTarget(null);
      fetchPumpsWithParams(currentPage);
    } catch (err) {
      setSnackbar({ open: true, message: err.message, severity: "error" });
      setDeleteTarget(null);
    }
  };

  const columns = [
    { field: "id", headerName: "ID", minWidth: 40, flex: 0.4 },
    { field: "name", headerName: "Name", minWidth: 170, flex: 1 },
    { field: "imei", headerName: "IMEI No", minWidth: 140, flex: 1 },
    { field: "total_running_time", headerName: "Total Run Time", minWidth: 80, flex: 1 },
    { field: "total_water_discharge", headerName: "Total Water Discharged (L)", minWidth: 80, flex: 1 },
    {
      field: "pump_running",
      headerName: "Status",
      minWidth: 60,
      flex: 0.5,
      renderCell: ({ row: { pump_running } }) => (
        <Box display="flex" alignItems="center" gap="8px">
          <Typography variant="body2">
            {pump_running === true ? 'ON':'OFF'}
          </Typography>
        </Box>
      ),
    },
    {
      field: "pump_connectivity",
      headerName: "Network",
      minWidth: 100,
      flex: 1,
      renderCell: ({ row: { pump_connectivity } }) => (
        <Box
          px="12px"
          py="2px"
          borderRadius="4px"
          bgcolor={pump_connectivity ? "green" : colors.redAccent[600]}
        >
          <Typography variant="body2" color={'white'}>
            {pump_connectivity ? "Connected" : "Disconnected"}
          </Typography>
        </Box>
      ),
    },
    { field: "consumer", headerName: "Customer", minWidth: 180, flex: 1 },
    {
      field: "view",
      headerName: "View",
      minWidth: 80,
      flex: 0.5,
      sortable: false,
      filterable: false,
      renderCell: (params) => (
        <IconButton
          onClick={() => handleRowClick(params)}
          sx={{ color: colors.blueAccent[300] }}
        >
          <VisibilityIcon />
        </IconButton>
      ),
    },
    ...(canDelete ? [{
      field: "delete",
      headerName: "Delete",
      minWidth: 80,
      flex: 0.5,
      sortable: false,
      filterable: false,
      renderCell: (params) => (
        <IconButton
          onClick={() => setDeleteTarget(params.row)}
          sx={{ color: "error.main" }}
        >
          <DeleteIcon />
        </IconButton>
      ),
    }] : []),
  ];

  const handleRowClick = (params) => {
    setSelectedRow(params.row);
    setValue(params.row.imei);
    navigate(`/individualPump/${params.row.imei}`);
  };

  const fetchPumpsWithParams = async (page) => {
    setSearchLoading(true);
    setSearchError(null);
    try {
      const searchQuery = searchTerm.trim() ? `&search=${encodeURIComponent(searchTerm)}` : '';
      const response = await authorizedFetch(`/pumps?page=${page}&limit=${limit}${searchQuery}`);
      const pumpLoaded = await response.json();
      if (response.ok) {
        if (pumpLoaded.pumps.length === 0) {
          setSearchError(searchTerm ? "No pumps found matching the search criteria" : "No pumps available");
          setPumps([]);
          setTotalPages(0);
          setTotalPumps(0);
        } else {
          const total = pumpLoaded.total || pumpLoaded.pumps.length;
          setPumps(pumpLoaded.pumps.map((pump, index) => ({
            ...pump,
            total_running_time: formatDuration(pump.total_running_time),
            total_water_discharge:(pump.total_water_discharge).toFixed(2),
            id: (page - 1) * limit + index + 1,
          })));
          setTotalPumps(total);
          setTotalPages(Math.ceil(total / limit));
          setSearchError(null);
        }
      } else {
        setSearchError("Failed to fetch pumps");
        setPumps([]);
        setTotalPages(0);
        setTotalPumps(0);
      }
    } catch (error) {
      console.error("Error fetching pumps:", error);
      setSearchError("Failed to fetch pumps");
      setPumps([]);
      setTotalPages(0);
      setTotalPumps(0);
    } finally {
      setSearchLoading(false);
    }
  };

  const handleSearch = async () => {
    if (!searchTerm.trim()) {
      setSearchError("Please enter a valid Pump IMEI or name");
      setSearchTerm('');
      return;
    }
    setCurrentPage(1); // Reset to page 1 on new search
    await fetchPumpsWithParams(1);
    // setSearchTerm(''); // Clear search term after search
  };

  useEffect(() => {
    if (fetchTimeout.current) {
      clearTimeout(fetchTimeout.current);
    }
    fetchTimeout.current = setTimeout(() => {
      setCurrentPage(1);
      fetchPumpsWithParams(1);
    }, 300);
    return () => {
      if (fetchTimeout.current) {
        clearTimeout(fetchTimeout.current);
      }
    };
  }, [searchTerm]);

  const handleNextPage = () => {
    if (currentPage < totalPages) {
      const nextPage = currentPage + 1;
      setCurrentPage(nextPage);
      fetchPumpsWithParams(nextPage);
    }
  };

  const handlePrevPage = () => {
    if (currentPage > 1) {
      const prevPage = currentPage - 1;
      setCurrentPage(prevPage);
      fetchPumpsWithParams(prevPage);
    }
  };

  return (
    <Box m="10px">
      <Header title="PUMP MONITORING" subtitle="Real-time Pump System Data" />
      <Box display="flex" alignItems="center" gap="10px" mb="20px">
        <TextField
          variant="outlined"
          size="small"
          placeholder="Enter pump name or IMEI to search"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          sx={{
            width: "40%",
            "& .MuiOutlinedInput-root": {
              backgroundColor: colors.primary[400],
              "& fieldset": {
                borderColor: colors.grey[100],
              },
              "&:hover fieldset": {
                borderColor: colors.blueAccent[300],
              },
            },
            "& .MuiInputBase-input": {
              color: colors.grey[100],
            },
          }}
        />
        {searchLoading && <CircularProgress size={20} color="primary" />}
        <Button
          variant="contained"
          color="primary"
          onClick={handleSearch}
          disabled={searchLoading}
          sx={{
            backgroundColor: colors.blueAccent[700],
            "&:hover": {
              backgroundColor: colors.blueAccent[600],
            },
            textTransform: "none",
            fontSize: "0.875rem",
            padding: "8px 16px",
          }}
        >
          {searchLoading ? <CircularProgress size={20} color="inherit" /> : "Search"}
        </Button>
      </Box>
      {searchError && (
        <Typography color="error" mb="10px">
          {searchError}
        </Typography>
      )}
     
      <Box
  m="20px 0 0 0"
  height="70vh"
  sx={{
    width: '100%',
    overflow: 'hidden', // Prevent any overflow on the container
    "& .MuiDataGrid-root": {
      border: "none",
      width: '100%',
      height: '100%',
      overflow: 'hidden', // Prevent root overflow
    },
    "& .MuiDataGrid-main": {
      overflow: 'hidden',
    },
    "& .MuiDataGrid-columnHeaders": {
      backgroundColor: colors.blueAccent[700],
      borderBottom: "none",
      fontSize: '0.9rem',
      position: 'sticky',
      top: 0,
      zIndex: 2,
      width: '100% !important', // Force full width
      minWidth: '100% !important',
      marginBottom:'-55px'
      // Remove the negative margin
    },
    "& .MuiDataGrid-columnHeadersInner": {
      backgroundColor: colors.blueAccent[700],
      width: '100% !important',
      // Compensate for scrollbar width
      paddingRight: '0px !important',
    },
    "& .MuiDataGrid-virtualScroller": {
      backgroundColor: colors.primary[400],
      overflow: 'auto !important',
      // Hide horizontal scrollbar
      overflowX: 'hidden !important',
      overflowY: 'auto !important',
      // Custom scrollbar styling
      '&::-webkit-scrollbar': {
        width: '8px',
      },
      '&::-webkit-scrollbar-track': {
        backgroundColor: colors.primary[400],
      },
      '&::-webkit-scrollbar-thumb': {
        backgroundColor: colors.blueAccent[600],
        borderRadius: '4px',
      },
      '&::-webkit-scrollbar-thumb:hover': {
        backgroundColor: colors.blueAccent[500],
      },
    },
    "& .MuiDataGrid-virtualScrollerContent": {
      height: 'auto !important',
      width: '100% !important',
    },
    "& .MuiDataGrid-virtualScrollerRenderZone": {
      width: '100% !important',
    },
    "& .MuiDataGrid-cell": {
      borderBottom: "none",
      fontSize: '0.875rem',
      whiteSpace: 'normal !important',
      wordWrap: 'break-word !important',
      lineHeight: '1.4 !important',
      padding: '8px !important',
      overflow: 'hidden', // Prevent cell content overflow
    },
    "& .MuiDataGrid-row": {
      width: '100% !important',
      maxWidth: '100% !important',
    },
    "& .MuiDataGrid-row:hover": {
      backgroundColor: colors.blueAccent[800],
    },
    "& .MuiDataGrid-footerContainer": {
      borderTop: "none",
      backgroundColor: colors.blueAccent[700],
      position: 'sticky',
      bottom: 0,
      zIndex: 1,
    },
    "& .MuiDataGrid-columnHeaderTitle": {
      whiteSpace: 'normal',
      lineHeight: '1.2',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
    },
    // Hide default scrollbars on the entire grid
    "& .MuiDataGrid-scrollArea": {
      display: 'none',
    },
    "& .MuiDataGrid-scrollArea--left": {
      display: 'none',
    },
    "& .MuiDataGrid-scrollArea--right": {
      display: 'none',
    },
  }}
>
  <DataGrid
    rows={pumps}
    columns={columns}
    disableSelectionOnClick
    disableExtendRowFullWidth
    hideFooterPagination
    hideFooterSelectedRowCount
    disableColumnResize // Prevent column resizing that might cause horizontal scroll
    disableColumnMenu // Optional: disable column menu
    sx={{
      '& .MuiDataGrid-columnHeader': {
        outline: 'none !important',
      },
      '& .MuiDataGrid-cell': {
        outline: 'none !important',
      },
    }}
  />
</Box>
      <Box display="flex" justifyContent="center" alignItems="center" gap="10px" mt="10px">
        <Button
          variant="contained"
          color="primary"
          onClick={handlePrevPage}
          disabled={searchLoading || currentPage === 1}
          sx={{
            backgroundColor: colors.blueAccent[700],
            "&:hover": {
              backgroundColor: colors.blueAccent[600],
            },
            textTransform: "none",
          }}
        >
          Previous  
        </Button>
        <Typography>
          Page {currentPage} of {totalPages} (Total Pumps: {totalPumps})
        </Typography>
        <Button
          variant="contained"
          color="primary"
          onClick={handleNextPage}
          disabled={searchLoading || currentPage === totalPages || totalPages === 0}
          sx={{
            backgroundColor: colors.blueAccent[700],
            "&:hover": {
              backgroundColor: colors.blueAccent[600],
            },
            textTransform: "none",
          }}
        >
          Next
        </Button>
      </Box>

      <Dialog open={!!deleteTarget} onClose={() => setDeleteTarget(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Delete Pump</DialogTitle>
        <DialogContent>
          <Typography>
            Delete pump <strong>{deleteTarget?.name}</strong> (IMEI: <strong>{deleteTarget?.imei}</strong>)?
            This will also remove all pump status data and Redis keys. This cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteTarget(null)}>Cancel</Button>
          <Button variant="contained" color="error" onClick={handleDeleteConfirm}>Delete</Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar(s => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar(s => ({ ...s, open: false }))}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Pumps;